import "@assets/css/bootstrap-grid.css";
import { InvoiceBox } from "@assets/icons";
import { Button } from "@components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  StudentInvoiceFormData,
  StudentInvoiceSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { InvoiceData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import PreviewCard from "../component/preview";
import { Switch } from "@components/ui/switch";
import { useReactToPrint } from "react-to-print";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Command } from "lucide-react";
import { METHODS } from "@services/interface.service";
import { Spinner } from "@components/ui/spinner";
const InvoiceForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { handleAction, searchParams } = useHandleAction({});
  const pageItem = searchParams.get("page-item");
  const pathname = usePathname();
  const isJrp = pathname == "jrp-invoice";

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<StudentInvoiceFormData>({
    mode: "onChange",
    resolver: zodResolver(StudentInvoiceSchema),
  });

  const printRef = useRef(null);

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<InvoiceData>
  >({
    endpoint: endpoint["invoice"],
    key: `${pathname}-${pageItem}`,
    enabled: pageItem ? true : false,
    id: pageItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (searchParams.get("page") == "add") {
      return reset({});
    }

    if (searchParams.get("page") !== "add") {
      return reset({
        professional_image: fetchData?.data?.student?.user?.professional_image,
        phone: fetchData?.data?.student?.user?.phone,
        full_name: fetchData?.data?.student?.user?.full_name,
        email: fetchData?.data?.student?.user?.email,
        total_amount: fetchData?.data?.total_amount ?? "",
        discount: fetchData?.data?.discount ?? "",
        paid_amount: fetchData?.data?.paid_amount ?? "",
      });
    }
  }, [fetchData, reset, setIsRetrive]);

  /* ---- billing correction: PATCH the invoice's own amounts ---- */
  const { mutate: saveBilling, isPending: savingBilling } = usePostData();

  const toNumber = (value: unknown) =>
    value === "" || value === undefined || value === null
      ? undefined
      : Number(value);

  // A field left blank is omitted from the PATCH, so the invoice keeps its stored
  // figure. Fall back to that same stored figure when checking the arithmetic,
  // otherwise clearing one box looks like setting it to zero.
  const effectiveAmounts = () => ({
    total_amount:
      toNumber(watch("total_amount")) ??
      toNumber(fetchData?.data?.total_amount) ??
      0,
    discount:
      toNumber(watch("discount")) ?? toNumber(fetchData?.data?.discount) ?? 0,
    paid_amount:
      toNumber(watch("paid_amount")) ??
      toNumber(fetchData?.data?.paid_amount) ??
      0,
  });

  const handleBillingSave = () => {
    if (!pageItem) return;

    const payload: Record<string, unknown> = { id: pageItem };
    const total_amount = toNumber(watch("total_amount"));
    const discount = toNumber(watch("discount"));
    const paid_amount = toNumber(watch("paid_amount"));

    if (total_amount !== undefined) payload.total_amount = total_amount;
    if (discount !== undefined) payload.discount = discount;
    if (paid_amount !== undefined) payload.paid_amount = paid_amount;

    if (Object.keys(payload).length === 1) {
      return showToast({
        title: "Nothing to update",
        description: "Enter at least one amount before saving.",
        type: "warning",
      });
    }

    if (
      [total_amount, discount, paid_amount].some(
        (value) => value !== undefined && (Number.isNaN(value) || value < 0),
      )
    ) {
      return showToast({
        title: "Invalid amount",
        description: "Amounts must be numbers of 0 or more.",
        type: "error",
      });
    }

    const effective = effectiveAmounts();
    if (effective.discount + effective.paid_amount > effective.total_amount) {
      return showToast({
        title: "Amounts don't add up",
        description:
          "Discount plus paid amount cannot exceed the total amount.",
        type: "error",
      });
    }

    saveBilling(
      {
        data: payload,
        endpoint: endpoint["invoice"],
        method: METHODS.PATCH,
      },
      {
        onSuccess: ({ status }: { status: number }) => {
          if ([200, 201, 202].includes(status)) {
            queryClient.invalidateQueries({
              queryKey: [`${pathname}-listing`],
            });
            queryClient.invalidateQueries({
              queryKey: [`${pathname}-${pageItem}`],
            });
            showToast({
              title: "Success",
              description: "Invoice amounts updated",
              type: "success",
            });
          }
        },
        onError: (error: Error & { response?: { data?: unknown } }) => {
          throwErrorAlert(error?.response?.data ?? error);
        },
      },
    );
  };

  const handleMutate = () => {
    setIsFormPending(true);
    return {
      data: { invoice_id: searchParams.get("page-item") },
      endpoint: endpoint["invoice-email"],
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      setIsFormPending(false);
      showToast({
        description: `Invoice Email sent`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "page-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
      setIsFormPending(false);
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };
  const { mutate } = usePostData();
  const onSubmit = () => {
    mutate(handleMutate(), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    onPrintError: (e) => {
      console.log("ERROR: error during printing");
    },
  });
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "p" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        reactToPrintFn();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  const formSchema: FormField[] = [
    {
      type: "component",
      component: <p className="text-[18px] font-[700]">Student Details</p>,
    },
    {
      name: "full_name",
      label: "Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Name",
      control: control,
      setValue: setValue,
      errors,
      disabled: true,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      disabled: true,
      visable: true,
    },
    {
      name: "phone",
      label: "Phone Number",
      colSpan: 12,
      type: "tel",
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      disabled: true,
      visable: true,
    },
    {
      name: "professional_image",
      label: "Upload Image",
      colSpan: 12,
      type: "cropper",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
      disabled: true,
    },
    {
      type: "component",
      component: (
        <div className="flex gap-2 items-center">
          <label
            htmlFor={isJrp ? "is_jrp" : "is_masterclass"}
            className="cursor-pointer"
          >
            {isJrp ? "Is JRP" : "Is Masterclass"}
          </label>
          <Controller
            control={control}
            name={isJrp ? "is_jrp" : "is_masterclass"}
            render={({}) => (
              <Switch
                id={isJrp ? "is_jrp" : "is_masterclass"}
                checked={true}
                onCheckedChange={() => {}}
                disabled
              />
            )}
          />
        </div>
      ),
      colSpan: 12,
      visable: ["masterclass-invoice", "jrp-invoice"].includes(pathname),
    },
    {
      type: "component",
      component: (
        <div className="mt-[10px]">
          <p className="text-[18px] font-[700]">Billing</p>
          <p className="text-[12px] text-muted-foreground">
            These amounts belong to this invoice only. Correcting them here does
            not change the course price, and the due amount is recalculated for
            you.
          </p>
        </div>
      ),
      colSpan: 12,
      visable: pageItem ? true : false,
    },
    {
      name: "total_amount",
      label: "Total Amount",
      colSpan: 12,
      type: "number",
      placeholder: "Enter Total Amount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: pageItem ? true : false,
    },
    {
      name: "discount",
      label: "Discount",
      colSpan: 6,
      type: "number",
      placeholder: "Enter Discount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: pageItem ? true : false,
    },
    {
      name: "paid_amount",
      label: "Paid Amount",
      colSpan: 6,
      type: "number",
      placeholder: "Enter Paid Amount",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: pageItem ? true : false,
    },
    {
      type: "component",
      component: (
        <div className="flex items-center justify-between gap-3 mb-[20px]">
          <span className="text-[13px] text-muted-foreground">
            Due after saving:{" "}
            <b>
              {(() => {
                const { total_amount, discount, paid_amount } =
                  effectiveAmounts();
                return Math.max(
                  0,
                  total_amount - discount - paid_amount,
                ).toLocaleString();
              })()}
            </b>
          </span>
          <Button
            type="button"
            variant={"default"}
            onClick={handleBillingSave}
            disabled={savingBilling}
            endIcon={savingBilling ? <Spinner /> : null}
          >
            Update Invoice
          </Button>
        </div>
      ),
      colSpan: 12,
      visable: pageItem ? true : false,
    },
    {
      type: "component",
      component: (
        <div className="my-[20px]">
          <div className="flex justify-between items-center mb-[10px]">
            <p className="text-[18px] font-[700]">Preview</p>
            <Button type="button" variant={"outline"} onClick={reactToPrintFn}>
              {" "}
              <InvoiceBox /> Download PDF
            </Button>
          </div>

          <PreviewCard
            ref={contentRef}
            data={watch()}
            invoice={fetchData?.data ?? {}}
          />
        </div>
      ),
    },
  ];

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );
  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        <Alert
          variant="default"
          className="max-md:m-4 bg-[#e8f6f1] text-[#1daa7e] my-4"
        >
          <AlertTitle>Heads up!</AlertTitle>
          <AlertDescription className="flex gap-2  text-[#1daa7e]">
            You can print the invoice by pressing{" "}
            <Command size={16} className="m-0" />/ Ctrl + p.
          </AlertDescription>
        </Alert>
        <div className="col-md-5">
          <div className="row">
            {formSchema.slice(0, 11).map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
        </div>
        <div className="col-md-7">
          <div className="row">
            {formSchema.slice(11, 12).map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
        </div>

        {/* {formSchema.slice(6, 10).map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )} */}
      </form>
    </>
  );
};

export default InvoiceForm;
